import { debounce } from 'lodash';
import { useStore } from '../store/store';
import { getBoxById } from '../store/lookups';
import { textToRaw, triggersToRaw } from '../utils/dataSync';
import { removeDuplicatesInVariableOrderArray } from '../utils/variableOrderArray';
import {
  patchBoxRequest,
  patchStructureRequest,
} from '../requests/proposals';
import { docIdOf } from './localEditGate';
import {
  addDiffKeys,
  createPendingKeys,
  isPendingEmpty,
  materializeDiff,
  mergePendingKeys,
} from './pendingKeys';

// Persistance granulaire (autosave). Format REST = format déjà stocké dans
// content (sortie de textToRaw / triggersToRaw, comme le POST blob) — distinct
// du format wire du channel (entitySerializer). Chaque acteur persiste
// uniquement ses entités : un diff reçu d'un co-éditeur n'est jamais persisté
// (la persistance est branchée sur le pipeline d'émission, pas de réception).
//
// Une seule file, une requête à la fois : diffs structurels (patch_structure)
// et brouillons de frappe (patch_box, qui ne remplace que la box visée, même
// dans un mask partagé) arrivent au serveur dans l'ordre. La file garde des
// clés et des brouillons, pas des requêtes : chaque envoi est reconstruit
// depuis l'état courant (cf. pendingKeys.js). Un échec réseau ou serveur est
// rejoué jusqu'au succès ou au changement de document, sans jamais renvoyer
// un état périmé.
const BOX_DRAFT_DEBOUNCE_MS = 2000;
const RETRY_MIN_MS = 2000;
const RETRY_MAX_MS = 30000;

const documentTarget = () => {
  const { currentDocument, currentProposal } = useStore.getState();
  return currentDocument.type === 'template'
    ? { documentType: 'template', documentId: currentDocument.id }
    : { documentType: 'proposal', documentId: currentProposal.id };
};

// Requêtes de persistance en vol du document courant. Le statut « en cours
// d'enregistrement » est vrai dès qu'au moins une requête tourne. Les promesses
// suivies ne rejettent jamais. Remplacé au changement de document : une
// requête du document quitté ne bloque pas celui qu'on ouvre.
let inFlightSaves = new Set();

// Clés structurelles modifiées localement pas encore envoyées, et document
// auquel la file appartient.
let pending = createPendingKeys();
let pendingDocId = null;
// Brouillons de frappe (raw DraftJS) par box. Le texte tapé n'entre dans le
// store qu'à la désélection : tant que la box est sélectionnée, son brouillon
// est posé sur elle à chaque envoi qui la contient (diff structurel compris —
// un changement de style ne synchronise pas le texte). Oublié quand un diff
// de la box arrive alors qu'elle n'est plus sélectionnée : le store l'a
// rattrapé.
let drafts = new Map();
// Boxes dont le brouillon n'est pas encore enregistré côté serveur.
let draftsToSend = new Set();
// Un brouillon attend encore son debounce, ou n'a pas pu partir (session
// révoquée) : du texte n'existe que dans l'éditeur.
let draftWaiting = false;
let requestInFlight = false;
let retryTimer = null;
let retryDelay = RETRY_MIN_MS;
// Vrai entre un échec (ou une modification retenue hors connexion) et le
// premier envoi réussi qui laisse tout enregistré.
let failing = false;
// Refusé par le serveur (4xx) : rejouer automatiquement ne changerait rien.
// Mis de côté pour ne pas bloquer les envois suivants, renvoyé sur demande
// (bouton « Enregistrer », cf. requeueRejectedSaves).
let rejectedKeys = createPendingKeys();
let rejectedDrafts = new Set();
// Incrémenté au changement de document : une réponse tardive du document
// quitté ne doit toucher ni la file ni le statut du document affiché.
let generation = 0;

const hasQueued = () => !isPendingEmpty(pending) || draftsToSend.size > 0;
const hasRejected = () =>
  !isPendingEmpty(rejectedKeys) || rejectedDrafts.size > 0;
const isAllSaved = () =>
  !hasQueued() && !requestInFlight && !hasRejected() && !draftWaiting;

// Statut de l'indicateur : false (tout est enregistré ou en cours d'envoi
// normal), 'retrying' (des modifications attendent un nouvel essai ou la
// reconnexion), 'rejected'. L'interface (SaveIndicator) en tire libellé,
// notification et bouton.
const refreshStatus = () => {
  const status = hasRejected() ? 'rejected' : failing ? 'retrying' : false;
  const store = useStore.getState();
  if (store.autosaveFailed !== status) store.setAutosaveFailed(status);
};

// Fin d'une série d'échecs : tout est enregistré, la reprise est signalée.
const settleFailure = () => {
  if (failing && isAllSaved()) {
    failing = false;
    useStore.getState().markAutosaveRecovered();
  }
  refreshStatus();
};

const clearRetry = () => {
  clearTimeout(retryTimer);
  retryTimer = null;
};

// Changement de document ou vidage du store pour un nouveau modèle : la file
// ne correspond plus au store, elle est abandonnée.
export const resetPendingSaves = () => {
  generation += 1;
  inFlightSaves = new Set();
  pending = createPendingKeys();
  pendingDocId = null;
  drafts = new Map();
  draftsToSend = new Set();
  draftWaiting = false;
  debouncedBoxDraft.cancel();
  requestInFlight = false;
  clearRetry();
  retryDelay = RETRY_MIN_MS;
  failing = false;
  rejectedKeys = createPendingKeys();
  rejectedDrafts = new Set();
  useStore.getState().setAutosaving(false);
  useStore.getState().setAutosaveFailed(false);
};

// Restauration de version : les brouillons portent du texte pré-restore qui
// écraserait la version restaurée. Les clés structurelles, elles, restent :
// suspendues pendant la restauration, elles sont reconstruites ensuite depuis
// le store rechargé (réécriture à l'identique) ou, si la restauration échoue,
// depuis les modifications locales toujours présentes.
export const discardDrafts = () => {
  drafts = new Map();
  draftsToSend = new Set();
  rejectedDrafts = new Set();
  draftWaiting = false;
  debouncedBoxDraft.cancel();
  refreshStatus();
};

const trackSave = (promise) => {
  const saves = inFlightSaves;
  if (saves.size === 0) useStore.getState().setAutosaving(true);
  saves.add(promise);
  promise.then(() => {
    saves.delete(promise);
    if (saves === inFlightSaves && saves.size === 0) {
      useStore.getState().setAutosaving(false);
    }
  });
  return promise;
};

// Une coupure réseau (pas de réponse), une erreur serveur ou une surcharge
// passent avec le temps ; une autre 4xx ne passera pas au prochain essai.
const isRetryable = (error) => {
  const status = error?.response?.status;
  return !status || status >= 500 || status === 408 || status === 429;
};

// Envoi suspendu tant que le canal collab est coupé (les modifications partent
// à la reconnexion, verrous repris), pendant un (re)chargement ou une
// restauration — le store vidé ou remplacé ferait partir les entités en
// attente comme des suppressions ou écraserait la version restaurée — et si
// la file vient d'un autre document.
const canSendNow = () => {
  const state = useStore.getState();
  return (
    state.collabConnected &&
    !state.sessionRevoked &&
    !state.isDocumentLoading &&
    !state.isRestoringVersion &&
    !state.proposalList &&
    docIdOf(state) === pendingDocId
  );
};

// Le délai court même si l'envoi est suspendu à son terme (chargement en
// cours) : on replanifie plutôt que de laisser la file sans nouvel essai.
const scheduleRetry = () => {
  if (retryTimer) return;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    if (canSendNow()) sendPending();
    else if (hasQueued()) scheduleRetry();
  }, retryDelay);
  retryDelay = Math.min(retryDelay * 2, RETRY_MAX_MS);
};

const onFailure = (error, { retry, reject }) => {
  console.error('Collab autosave failed', error);
  if (isRetryable(error)) {
    retry();
    failing = true;
    scheduleRetry();
  } else {
    reject();
  }
};

// Les réponses de sauvegarde portent `hidden` (opportunité cachée). Les modèles
// (templates) répondent sans corps : rien à mettre à jour. Une réponse tardive
// d'un document quitté entre-temps ne doit pas marquer le document affiché.
const syncHidden = (data, { documentType, documentId }) => {
  if (typeof data?.hidden !== 'boolean' || documentType !== 'proposal') return;
  if (useStore.getState().currentProposal?.id !== documentId) return;
  useStore.getState().setDocumentHidden(data.hidden);
};

// Boxes au format REST, brouillons posés dessus. `sent` reçoit, par box
// réellement envoyée, le brouillon posé (ou STORE_VERSION) : seules elles
// sont acquittées par la réponse.
const STORE_VERSION = Symbol('store version');
const boxesToRaw = (boxes, sent) =>
  textToRaw(boxes).map((box) => {
    const draft = drafts.get(box.id);
    sent.set(box.id, draft === undefined ? STORE_VERSION : draft);
    if (draft === undefined) return box;
    return { ...box, content: { ...box.content, raw: draft } };
  });

const serializeDiffForRest = (diff, applied) => {
  const out = { ...diff, upserts: { ...diff.upserts } };
  if (out.upserts.boxes) {
    out.upserts.boxes = boxesToRaw(out.upserts.boxes, applied);
  }
  if (out.upserts.masks) {
    out.upserts.masks = out.upserts.masks.map((m) => ({
      ...m,
      boxes: m.boxes ? boxesToRaw(m.boxes, applied) : [],
    }));
  }
  if (out.upserts.triggers) {
    out.upserts.triggers = triggersToRaw(
      out.upserts.triggers,
      useStore.getState().boxes
    );
  }
  if (out.values?.variableOrderArray) {
    out.values = {
      ...out.values,
      variableOrderArray: removeDuplicatesInVariableOrderArray(
        out.values.variableOrderArray
      ),
    };
  }
  return out;
};

// Une box envoyée est enregistrée si son texte n'a pas été retapé
// entre-temps : même brouillon, ou plus de brouillon (le store, envoyé, l'a
// rattrapé).
const acknowledgeDrafts = (sent) => {
  sent.forEach((raw, boxId) => {
    const current = drafts.get(boxId);
    if (current === undefined || current === raw) draftsToSend.delete(boxId);
  });
};

// Lance une requête de la file. `onSuccess` / `onError` ne tournent que si le
// document n'a pas changé entre-temps ; ensuite la file continue.
const runRequest = (request, target, { onSuccess, onError }) => {
  requestInFlight = true;
  const sentGeneration = generation;
  trackSave(
    request
      .then(
        (data) => {
          if (sentGeneration !== generation) return;
          syncHidden(data, target);
          retryDelay = RETRY_MIN_MS;
          onSuccess();
        },
        (error) => {
          if (sentGeneration === generation) onError(error);
        }
      )
      .then(() => {
        if (sentGeneration !== generation) return;
        requestInFlight = false;
        // La suite de la file (pas après un échec : le nouvel essai attend
        // son délai).
        if (!retryTimer) sendPending();
        settleFailure();
      })
      .catch((error) => console.error('Collab autosave failed', error))
  );
};

const sendStructure = (target) => {
  const keys = pending;
  pending = createPendingKeys();
  const diff = materializeDiff(keys, useStore.getState());
  if (!diff) return false;
  const applied = new Map();
  const request = patchStructureRequest({
    ...target,
    diff: serializeDiffForRest(diff, applied),
  });
  runRequest(request, target, {
    onSuccess: () => acknowledgeDrafts(applied),
    onError: (error) =>
      onFailure(error, {
        retry: () => mergePendingKeys(pending, keys),
        reject: () => mergePendingKeys(rejectedKeys, keys),
      }),
  });
  return true;
};

// Brouillon d'une box : la box du store (à jour hors contenu) sérialisée via
// textToRaw, avec le texte tapé par-dessus. patch_box ne remplace que cette
// box, y compris dans un mask.
const sendDraft = (target) => {
  const [boxId] = draftsToSend;
  const { boxes, masks } = useStore.getState();
  const box = getBoxById(boxes, masks, boxId);
  if (!box) {
    // Box supprimée entre-temps : sa suppression part par le diff structurel.
    draftsToSend.delete(boxId);
    return false;
  }
  const applied = new Map();
  const [serialized] = boxesToRaw([box], applied);
  const request = patchBoxRequest({ ...target, box: serialized });
  runRequest(request, target, {
    onSuccess: () => acknowledgeDrafts(applied),
    onError: (error) =>
      onFailure(error, {
        retry: () => {},
        reject: () => {
          draftsToSend.delete(boxId);
          rejectedDrafts.add(boxId);
        },
      }),
  });
  return true;
};

// Envoie le prochain élément de la file : diff structurel d'abord (il porte
// aussi les brouillons des boxes qu'il contient), puis les brouillons seuls.
const sendPending = () => {
  if (requestInFlight || !canSendNow()) return;
  clearRetry();
  const target = documentTarget();
  while (hasQueued()) {
    const sent = !isPendingEmpty(pending)
      ? sendStructure(target)
      : sendDraft(target);
    if (sent) return;
  }
  settleFailure();
};

// Le prochain envoi part tout de suite, sauf pendant le délai d'un nouvel
// essai : continuer à modifier ne doit pas contourner l'espacement des
// essais. Hors connexion, la modification est retenue et signalée.
const afterEnqueue = () => {
  if (!retryTimer) sendPending();
  if (!useStore.getState().collabConnected) failing = true;
  refreshStatus();
};

// Rattache la file au document courant quand elle était vide.
const claimQueue = (docId) => {
  if (!hasQueued() && !hasRejected()) pendingDocId = docId;
};

// Diff structurel local (appelé par structureSync au même debounce que le
// broadcast, ou hors connexion — l'acteur du diff est le seul writer de ses
// entités). Une box du diff qui n'est plus sélectionnée porte l'état le plus
// frais du store : son brouillon est rattrapé.
export const persistStructureDiff = (diff) => {
  const docId = docIdOf(useStore.getState());
  if (!docId) return;
  const { selectedBoxId } = useStore.getState();
  const caughtUp = (box) => {
    if (box.id !== selectedBoxId) drafts.delete(box.id);
  };
  (diff.upserts?.boxes ?? []).forEach(caughtUp);
  (diff.upserts?.masks ?? []).forEach((mask) =>
    (mask.boxes ?? []).forEach(caughtUp)
  );
  claimQueue(docId);
  addDiffKeys(pending, diff);
  afterEnqueue();
};

// Nouvel essai immédiat (reconnexion, retour en ligne, départ du document,
// bouton). Si l'envoi est encore impossible (chargement), le nouvel essai
// reste planifié.
export const retryPendingSaves = () => {
  clearRetry();
  sendPending();
  if (!requestInFlight && hasQueued()) scheduleRetry();
};

// Remet en file ce que le serveur avait refusé (enregistrement manuel).
export const requeueRejectedSaves = () => {
  if (!hasRejected()) return;
  if (!hasQueued()) pendingDocId = docIdOf(useStore.getState());
  mergePendingKeys(pending, rejectedKeys);
  rejectedKeys = createPendingKeys();
  rejectedDrafts.forEach((boxId) => draftsToSend.add(boxId));
  rejectedDrafts = new Set();
  failing = true;
  refreshStatus();
};

// Texte tapé pas encore enregistré pour une box (relais aux co-éditeurs à la
// reconnexion).
export const unsavedDraftOf = (boxId) =>
  draftsToSend.has(boxId) ? drafts.get(boxId) : undefined;

// Vrai tant que quelque chose n'est pas encore enregistré (alerte à la
// fermeture de l'onglet).
export const hasUnsavedChanges = () =>
  inFlightSaves.size > 0 || !isAllSaved();

// Attend les requêtes en vol (y compris celles lancées pendant l'attente) et
// dit si tout ce qui a été modifié sur ce document est enregistré.
export const waitForPendingSaves = async () => {
  while (inFlightSaves.size > 0) {
    await Promise.all([...inFlightSaves]);
  }
  return isAllSaved();
};

const persistBoxDraft = (boxId, raw, docId, queuedGeneration) => {
  draftWaiting = false;
  // Tapé sur un document quitté ou restauré depuis : ne part pas.
  if (queuedGeneration !== generation) return;
  if (docIdOf(useStore.getState()) !== docId) return;
  claimQueue(docId);
  drafts.set(boxId, raw);
  draftsToSend.add(boxId);
  afterEnqueue();
};

const debouncedBoxDraft = debounce(persistBoxDraft, BOX_DRAFT_DEBOUNCE_MS);

// Autosave du brouillon de la box en cours de frappe (debounce 2 s, flush au
// release du verrou).
export const queueBoxDraftPersist = (boxId, raw) => {
  const docId = docIdOf(useStore.getState());
  if (!docId) return;
  draftWaiting = true;
  debouncedBoxDraft(boxId, raw, docId, generation);
};

// Flush au release du verrou (désélection) : le dernier état tapé part
// immédiatement ; l'état final du store suivra via le diff structurel.
export const flushBoxDraftPersist = () => debouncedBoxDraft.flush();

// Session révoquée : plus aucune écriture. Le brouillon en attente n'est pas
// envoyé, mais il reste signalé comme non enregistré (alerte à la fermeture).
export const cancelBoxDraftPersist = () => debouncedBoxDraft.cancel();
